import { useState } from 'react';
import {
	CERTIFICATION_FLAG_BY_PROOF,
	proofToggleFlags,
	type KycProofFlag,
} from '@dealstack/shared/lib/common/business/kycCertification';
import { BankAccountApprovalMode } from '@dealstack/shared/lib/generated';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { KycSettings } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/types.ts';
import {
	IndividualInvestorSettings,
	InvestmentVehicleSettings,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/InvestorData/types.ts';
import {
	useOrganizationKycSetting,
	useUpdateOrganizationKycSetting,
} from '@dealstack/frontend/app/pages/Organization/OrganizationProgrammeDefaults/hooks/useOrganizationKycSetting';
import {
	BooleanColumn,
	Column,
	INDIVIDUAL_COLUMNS,
	KYC_COLUMNS,
	VEHICLE_COLUMNS,
} from '@dealstack/frontend/app/pages/Organization/OrganizationProgrammeDefaults/hooks/useOrganizationRequirements.utils';

interface Props {
	organizationId: string;
	canEdit: boolean;
}

type ColumnChanges = Partial<Record<BooleanColumn, boolean>>;

const isProofColumn = (column: BooleanColumn): column is KycProofFlag & BooleanColumn =>
	column in CERTIFICATION_FLAG_BY_PROOF;

/**
 * Produces the same shapes the programme tier's controllers do, so both tiers render the shared
 * KycSettingsView / InvestorDataRequirementsList and cannot drift in wording or ordering.
 */
export const useOrganizationRequirements = ({ organizationId, canEdit }: Props) => {
	const tMipManage = useTranslationMipManage();
	const { data: requirements, isLoading } = useOrganizationKycSetting(organizationId);
	// Respond immediately rather than waiting for the refetch, as the programme tier does.
	const [optimistic, setOptimistic] = useState<ColumnChanges>({});

	/**
	 * Drops this save's keys, handing those switches back to the server's values (already
	 * refetched on success; reverted on failure). Left in place they'd shadow the real
	 * organization-tier default indefinitely.
	 */
	const releaseOverlay = (changes: ColumnChanges) =>
		setOptimistic((current) =>
			Object.fromEntries(Object.entries(current).filter(([key]) => !(key in changes)))
		);

	// On the mutation rather than per `mutate` call: in TanStack v5 a per-call callback fires only
	// for the latest call, so two quick toggles where the first fails left the first switch
	// showing a value that never persisted.
	const { mutate, mutateAsync, isPending, variables } = useUpdateOrganizationKycSetting(
		organizationId,
		{
			onSuccess: (changes: ColumnChanges) => releaseOverlay(changes),
			onError: (changes: ColumnChanges) => {
				releaseOverlay(changes);
				showToastV2({
					state: 'error',
					title: tMipManage('kyc_settings.requirements.updated_error'),
				});
			},
		}
	);
	// Kept apart from the toggle map above, which is boolean-only.
	const [optimisticApprovalMode, setOptimisticApprovalMode] = useState<BankAccountApprovalMode>();

	const isOn = (column: Column) => optimistic[column] ?? Boolean(requirements?.[column]);

	// Only the columns this save is carrying, so one switch in flight does not grey out the rest.
	const inFlight = isPending ? variables : undefined;
	const isSaving = (column: Column) => !!inFlight && column in inFlight;

	const toggleColumn = (column: BooleanColumn) => {
		const next = !isOn(column);
		// A certification is meaningless without the proof it certifies, so a proof carries its
		// certification with it — the same rule the repository enforces on write. Without it the
		// switch would keep showing a certification the save had just cleared.
		const changes: ColumnChanges = isProofColumn(column)
			? proofToggleFlags(column, next)
			: { [column]: next };

		setOptimistic((current) => ({ ...current, ...changes }));
		mutate(changes);
	};

	// Nothing to revert — the row either exists afterwards or the page stays on its empty state —
	// but a silent failure would read as the save having worked.
	const startFromDefaults = () =>
		mutate(
			{},
			{
				onError: () =>
					showToastV2({
						state: 'error',
						title: tMipManage('kyc_settings.requirements.updated_error'),
					}),
			}
		);

	const settingsFor = <TKey extends string>(columns: Record<TKey, BooleanColumn>) =>
		Object.entries<BooleanColumn>(columns).reduce(
			(accumulator, [key, column]) => ({
				...accumulator,
				[key]: { id: key, checked: isOn(column), disabled: isSaving(column) },
			}),
			{} as Record<TKey, { id: string; checked: boolean; disabled: boolean }>
		);

	return {
		isLoading,
		isConfigured: !!requirements,
		hasPermission: canEdit,
		// Creates the row with the schema defaults, so nothing here restates them.
		startFromDefaults,

		settings: settingsFor(KYC_COLUMNS) as KycSettings,
		toggleSetting: (key: keyof KycSettings) => toggleColumn(KYC_COLUMNS[key]),
		certificationText: {
			savedText: requirements?.certificationText ?? null,
			save: (certificationText: string | null) => mutateAsync({ certificationText }),
			isSaving: isSaving('certificationText'),
		},

		bankAccountApproval: {
			value:
				optimisticApprovalMode ??
				requirements?.bankAccountApprovalMode ??
				BankAccountApprovalMode.AUTO_APPROVE_ALL,
			disabled: isPending,
		},
		setBankAccountApproval: (value: BankAccountApprovalMode) => {
			setOptimisticApprovalMode(value);
			mutate({ bankAccountApprovalMode: value });
		},

		individualSettings: settingsFor(INDIVIDUAL_COLUMNS) as IndividualInvestorSettings,
		vehicleSettings: settingsFor(VEHICLE_COLUMNS) as InvestmentVehicleSettings,
		toggleIndividualSetting: (key: keyof IndividualInvestorSettings) =>
			toggleColumn(INDIVIDUAL_COLUMNS[key]),
		toggleVehicleSetting: (key: keyof InvestmentVehicleSettings) =>
			toggleColumn(VEHICLE_COLUMNS[key]),
	};
};
